/*
 * Copyright (c) Nexign, JSC, 2019-2020.
 */

/**
 *
 *
 * @author Yakov Gushchin
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @version 1.4.0
 * @since 1.0.1
 */

import styled from 'styled-components';

import { globalThemeConstants } from '../../../theme';

export interface ILayoutProps {
    cursor?: string;
    background?: string;
    border?: string;
    borderBottom?: string;
    borderLeft?: string;
    borderRadius?: string;
    borderRight?: string;
    borderTop?: string;
    boxSizing?: string;
    height?: string;
    margin?: string;
    marginBottom?: string;
    marginLeft?: string;
    marginRight?: string;
    marginTop?: string;
    maxHeight?: string;
    maxWidth?: string;
    minHeight?: string;
    minWidth?: string;
    padding?: string;
    paddingBottom?: string;
    paddingLeft?: string;
    paddingRight?: string;
    paddingTop?: string;
    width?: string;
}

/**
 * ---------------------------------------
 *               SPACING
 * ---------------------------------------
 *
 * @property m - for classes that set margin
 * @property p - for classes that set padding
 *
 * @sides
 * t - for classes that set margin-top or padding-top
 * b - for classes that set margin-bottom or padding-bottom
 * l - for classes that set margin-left or padding-left
 * r - for classes that set margin-right or padding-right
 * x - for classes that set both *-left and *-right
 * y - for classes that set both *-top and *-bottom
 *
 * @size
 * 0 - for classes that eliminate the margin or padding by setting it to 0
 * 1 - (by default) for classes that set the margin or padding to $spacer * .25
 * 2 - (by default) for classes that set the margin or padding to $spacer * .5
 * 3 - (by default) for classes that set the margin or padding to $spacer
 * 4 - (by default) for classes that set the margin or padding to $spacer * 1.5
 * 5 - (by default) for classes that set the margin or padding to $spacer * 2
 *
 * auto - for classes that set the margin to auto
 */

const spacer = globalThemeConstants.gutter * 2 || 16;

export type classes =
    'mt-0' | 'mt-1' | 'mt-2' | 'mt-3' | 'mt-4' | 'mt-5'
    | 'mr-0' | 'mr-1' | 'mr-2' | 'mr-3' | 'mr-4' | 'mr-5'
    | 'mb-0' | 'mb-1' | 'mb-2' | 'mb-3' | 'mb-4' | 'mb-5'
    | 'ml-0' | 'ml-1' | 'ml-2' | 'ml-3' | 'ml-4' | 'ml-5'
    | 'mx-0' | 'mx-1' | 'mx-2' | 'mx-3' | 'mx-4' | 'mx-5'
    | 'my-0' | 'my-1' | 'my-2' | 'my-3' | 'my-4' | 'my-5'
    | 'm-auto'
    | 'pt-0' | 'pt-1' | 'pt-2' | 'pt-3' | 'pt-4' | 'pt-5'
    | 'pr-0' | 'pr-1' | 'pr-2' | 'pr-3' | 'pr-4' | 'pr-5'
    | 'pb-0' | 'pb-1' | 'pb-2' | 'pb-3' | 'pb-4' | 'pb-5'
    | 'pl-0' | 'pl-1' | 'pl-2' | 'pl-3' | 'pl-4' | 'pl-5'
    | 'px-0' | 'px-1' | 'px-2' | 'px-3' | 'px-4' | 'px-5'
    | 'py-0' | 'py-1' | 'py-2' | 'py-3' | 'py-4' | 'py-5'

export const LayoutStyled = styled.div<ILayoutProps>`
    cursor: ${props => props.cursor};
    background: ${props => props.background};
    border-bottom: ${props => props.borderBottom};
    border-left: ${props => props.borderLeft};
    border-radius: ${props => props.borderRadius};
    border-right: ${props => props.borderRight};
    border-top: ${props => props.borderTop};
    border: ${props => props.border};
    box-sizing: ${props => props.boxSizing};
    height: ${props => props.height};
    margin-bottom: ${props => props.marginBottom};
    margin-left: ${props => props.marginLeft};
    margin-right: ${props => props.marginRight};
    margin-top: ${props => props.marginTop};
    margin: ${props => props.margin};
    max-height: ${props => props.maxHeight};
    max-width: ${props => props.maxWidth};
    min-height: ${props => props.minHeight};
    min-width: ${props => props.minWidth};
    padding-bottom: ${props => props.paddingBottom};
    padding-left: ${props => props.paddingLeft};
    padding-right: ${props => props.paddingRight};
    padding-top: ${props => props.paddingTop};
    padding: ${props => props.padding};
    width: ${props => props.width};

    /* MARGIN */
    &.mt-0 { margin-top: 0; }
    &.mt-1 { margin-top: ${spacer * .25}px; }
    &.mt-2 { margin-top: ${spacer * .5}px; }
    &.mt-3 { margin-top: ${spacer}px; }
    &.mt-4 { margin-top: ${spacer * 1.5}px; }
    &.mt-5 { margin-top: ${spacer * 2}px; }

    &.mr-0 { margin-right: 0; }
    &.mr-1 { margin-right: ${spacer * .25}px; }
    &.mr-2 { margin-right: ${spacer * .5}px; }
    &.mr-3 { margin-right: ${spacer}px; }
    &.mr-4 { margin-right: ${spacer * 1.5}px; }
    &.mr-5 { margin-right: ${spacer * 2}px; }

    &.mb-0 { margin-bottom: 0; }
    &.mb-1 { margin-bottom: ${spacer * .25}px; }
    &.mb-2 { margin-bottom: ${spacer * .5}px; }
    &.mb-3 { margin-bottom: ${spacer}px; }
    &.mb-4 { margin-bottom: ${spacer * 1.5}px; }
    &.mb-5 { margin-bottom: ${spacer * 2}px; }

    &.ml-0 { margin-left: 0; }
    &.ml-1 { margin-left: ${spacer * .25}px; }
    &.ml-2 { margin-left: ${spacer * .5}px; }
    &.ml-3 { margin-left: ${spacer}px; }
    &.ml-4 { margin-left: ${spacer * 1.5}px; }
    &.ml-5 { margin-left: ${spacer * 2}px; }

    &.mx-0 { margin-left: 0;                 margin-right: 0;}
    &.mx-1 { margin-left: ${spacer * .25}px; margin-right: ${spacer * .25}px; }
    &.mx-2 { margin-left: ${spacer * .5}px;  margin-right: ${spacer * .5}px; }
    &.mx-3 { margin-left: ${spacer}px;       margin-right: ${spacer}px; }
    &.mx-4 { margin-left: ${spacer * 1.5}px; margin-right: ${spacer * 1.5}px; }
    &.mx-5 { margin-left: ${spacer * 2}px;   margin-right: ${spacer * 2}px; }

    &.my-0 { margin-top: 0;                 margin-bottom: 0;}
    &.my-1 { margin-top: ${spacer * .25}px; margin-bottom: ${spacer * .25}px; }
    &.my-2 { margin-top: ${spacer * .5}px;  margin-bottom: ${spacer * .5}px; }
    &.my-3 { margin-top: ${spacer}px;       margin-bottom: ${spacer}px; }
    &.my-4 { margin-top: ${spacer * 1.5}px; margin-bottom: ${spacer * 1.5}px; }
    &.my-5 { margin-top: ${spacer * 2}px;   margin-bottom: ${spacer * 2}px; }

    &.mx-auto { margin-left: auto;   margin-right: auto; }

    /* PADDING */
    &.pt-0 { padding-top: 0; }
    &.pt-1 { padding-top: ${spacer * .25}px; }
    &.pt-2 { padding-top: ${spacer * .5}px; }
    &.pt-3 { padding-top: ${spacer}px; }
    &.pt-4 { padding-top: ${spacer * 1.5}px; }
    &.pt-5 { padding-top: ${spacer * 2}px; }

    &.pr-0 { padding-right: 0; }
    &.pr-1 { padding-right: ${spacer * .25}px; }
    &.pr-2 { padding-right: ${spacer * .5}px; }
    &.pr-3 { padding-right: ${spacer}px; }
    &.pr-4 { padding-right: ${spacer * 1.5}px; }
    &.pr-5 { padding-right: ${spacer * 2}px; }

    &.pb-0 { padding-bottom: 0; }
    &.pb-1 { padding-bottom: ${spacer * .25}px; }
    &.pb-2 { padding-bottom: ${spacer * .5}px; }
    &.pb-3 { padding-bottom: ${spacer}px; }
    &.pb-4 { padding-bottom: ${spacer * 1.5}px; }
    &.pb-5 { padding-bottom: ${spacer * 2}px; }

    &.pl-0 { padding-left: 0; }
    &.pl-1 { padding-left: ${spacer * .25}px; }
    &.pl-2 { padding-left: ${spacer * .5}px; }
    &.pl-3 { padding-left: ${spacer}px; }
    &.pl-4 { padding-left: ${spacer * 1.5}px; }
    &.pl-5 { padding-left: ${spacer * 2}px; }

    &.px-0 { padding-left: 0;                 padding-right: 0; }
    &.px-1 { padding-left: ${spacer * .25}px; padding-right: ${spacer * .25}px; }
    &.px-2 { padding-left: ${spacer * .5}px;  padding-right: ${spacer * .5}px; }
    &.px-3 { padding-left: ${spacer}px;       padding-right: ${spacer}px; }
    &.px-4 { padding-left: ${spacer * 1.5}px; padding-right: ${spacer * 1.5}px; }
    &.px-5 { padding-left: ${spacer * 2}px;   padding-right: ${spacer * 2}px; }

    &.py-0 { padding-top: 0;                 padding-bottom: 0; }
    &.py-1 { padding-top: ${spacer * .25}px; padding-bottom: ${spacer * .25}px; }
    &.py-2 { padding-top: ${spacer * .5}px;  padding-bottom: ${spacer * .5}px; }
    &.py-3 { padding-top: ${spacer}px;       padding-bottom: ${spacer}px; }
    &.py-4 { padding-top: ${spacer * 1.5}px; padding-bottom: ${spacer * 1.5}px; }
    &.py-5 { padding-top: ${spacer * 2}px;   padding-bottom: ${spacer * 2}px; }

    /* ALIGN */
    &.t-center { text-align: center; }
`;

export const LayoutBoxStyled = styled(LayoutStyled)<ILayoutProps>`
    box-sizing: border-box;
`;

export const LayoutGlobalStyled = styled(LayoutStyled)<ILayoutProps>`
    width: 100%;
    max-width: ${globalThemeConstants.maxSiteWidth};
    min-width: ${globalThemeConstants.minSiteWidth};
    margin: 0 auto;
    box-sizing: border-box;
`;
